import { delay } from '../utils/helpers';

class MockApiService {
  constructor() {
    this.storageKey = 'eureka_mock_data';
  }

  getData() {
    const data = localStorage.getItem(this.storageKey);
    return data ? JSON.parse(data) : null;
  }

  setData(data) {
    localStorage.setItem(this.storageKey, JSON.stringify(data));
  }

  addAuditLog(usuario, accion, entidad, entidadId, detalles) {
    const data = this.getData();
    if (!data) return;

    const auditLog = {
      id: `${Date.now()}-${Math.random()}`,
      timestamp: new Date().toISOString(),
      usuario: usuario,
      accion: accion,
      entidad: entidad,
      entidadId: entidadId,
      ip: '192.168.1.' + Math.floor(Math.random() * 255),
      userAgent: navigator.userAgent,
      detalles: detalles
    };

    data.auditLogs = [auditLog, ...data.auditLogs];
    this.setData(data);
    return auditLog;
  }

  async getConfiguracion() {
    await delay(300);
    const data = this.getData();
    if (!data || !data.configuracion) {
      throw new Error('Configuración no encontrada');
    }
    return { success: true, data: data.configuracion };
  }

  async updateConfiguracion(seccion, valores, usuario) {
    await delay(500);
    const data = this.getData();
    if (!data || !data.configuracion) {
      throw new Error('Configuración no encontrada');
    }

    if (!data.configuracion[seccion]) {
      throw new Error(`Sección ${seccion} no encontrada`);
    }

    data.configuracion[seccion] = {
      ...data.configuracion[seccion],
      ...valores
    };

    this.setData(data);
    this.addAuditLog(
      usuario,
      'actualizar_configuracion',
      'configuracion',
      seccion,
      `Actualizada sección: ${seccion}`
    );

    return { success: true, data: data.configuracion[seccion] };
  }

  async getNivelesCobertura() {
    await delay(300);
    const data = this.getData();
    if (!data || !data.nivelesCobertura) {
      throw new Error('Niveles de cobertura no encontrados');
    }
    return { success: true, data: data.nivelesCobertura };
  }

  async updateNivelCobertura(nivelId, valores, usuario) {
    await delay(500);
    const data = this.getData();
    if (!data || !data.nivelesCobertura) {
      throw new Error('Niveles de cobertura no encontrados');
    }

    const index = data.nivelesCobertura.findIndex(n => n.id === nivelId);
    if (index === -1) {
      throw new Error('Nivel de cobertura no encontrado');
    }

    data.nivelesCobertura[index] = {
      ...data.nivelesCobertura[index],
      ...valores
    };

    this.setData(data);
    this.addAuditLog(
      usuario,
      'actualizar_nivel_cobertura',
      'nivel_cobertura',
      nivelId,
      `Actualizado nivel: ${data.nivelesCobertura[index].nombre}`
    );

    return { success: true, data: data.nivelesCobertura[index] };
  }

  async getUsuarios() {
    await delay(300);
    const data = this.getData();
    if (!data || !data.usuarios) {
      throw new Error('Usuarios no encontrados');
    }
    return { success: true, data: data.usuarios };
  }

  async createUsuario(usuarioData, adminEmail) {
    await delay(500);
    const data = this.getData();
    if (!data || !data.usuarios) {
      throw new Error('Usuarios no encontrados');
    }

    const emailExists = data.usuarios.some(u => u.email === usuarioData.email);
    if (emailExists) {
      throw new Error('El email ya está registrado');
    }

    const nuevoUsuario = {
      id: `${Date.now()}-${Math.random()}`,
      ...usuarioData,
      fechaRegistro: new Date().toISOString(),
      ultimoAcceso: null,
      activo: true
    };

    data.usuarios.push(nuevoUsuario);
    this.setData(data);
    this.addAuditLog(
      adminEmail,
      'crear_usuario',
      'usuario',
      nuevoUsuario.id,
      `Creado usuario: ${nuevoUsuario.email}`
    );

    return { success: true, data: nuevoUsuario };
  }

  async updateUsuario(usuarioId, valores, adminEmail) {
    await delay(500);
    const data = this.getData();
    if (!data || !data.usuarios) {
      throw new Error('Usuarios no encontrados');
    }

    const index = data.usuarios.findIndex(u => u.id === usuarioId);
    if (index === -1) {
      throw new Error('Usuario no encontrado');
    }

    if (valores.email && valores.email !== data.usuarios[index].email) {
      const emailExists = data.usuarios.some(u => u.email === valores.email && u.id !== usuarioId);
      if (emailExists) {
        throw new Error('El email ya está registrado');
      }
    }

    data.usuarios[index] = {
      ...data.usuarios[index],
      ...valores
    };

    this.setData(data);
    this.addAuditLog(
      adminEmail,
      'actualizar_usuario',
      'usuario',
      usuarioId,
      `Actualizado usuario: ${data.usuarios[index].email}`
    );

    return { success: true, data: data.usuarios[index] };
  }

  async deleteUsuario(usuarioId, adminEmail) {
    await delay(500);
    const data = this.getData();
    if (!data || !data.usuarios) {
      throw new Error('Usuarios no encontrados');
    }

    const index = data.usuarios.findIndex(u => u.id === usuarioId);
    if (index === -1) {
      throw new Error('Usuario no encontrado');
    }

    const usuarioEmail = data.usuarios[index].email;
    data.usuarios.splice(index, 1);

    this.setData(data);
    this.addAuditLog(
      adminEmail,
      'eliminar_usuario',
      'usuario',
      usuarioId,
      `Eliminado usuario: ${usuarioEmail}`
    );

    return { success: true, message: 'Usuario eliminado correctamente' };
  }

  async getAuditLogs(filtros = {}) {
    await delay(300);
    const data = this.getData();
    if (!data || !data.auditLogs) {
      throw new Error('Logs de auditoría no encontrados');
    }

    let logs = [...data.auditLogs];

    if (filtros.usuario) {
      logs = logs.filter(log => log.usuario === filtros.usuario);
    }

    if (filtros.accion) {
      logs = logs.filter(log => log.accion === filtros.accion);
    }

    if (filtros.entidad) {
      logs = logs.filter(log => log.entidad === filtros.entidad);
    }

    if (filtros.fechaDesde) {
      logs = logs.filter(log => new Date(log.timestamp) >= new Date(filtros.fechaDesde));
    }

    if (filtros.fechaHasta) {
      logs = logs.filter(log => new Date(log.timestamp) <= new Date(filtros.fechaHasta));
    }

    logs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

    const limit = filtros.limit || 100;
    const offset = filtros.offset || 0;
    const paginatedLogs = logs.slice(offset, offset + limit);

    return {
      success: true,
      data: paginatedLogs,
      total: logs.length,
      offset: offset,
      limit: limit
    };
  }

  async exportAuditLogs(filtros = {}) {
    await delay(800);
    const result = await this.getAuditLogs(filtros);
    
    const csv = this.convertToCSV(result.data);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `audit_logs_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    return { success: true, message: 'Logs exportados correctamente' };
  }

  convertToCSV(data) {
    if (!data || data.length === 0) return '';

    const headers = Object.keys(data[0]);
    const csvRows = [];

    csvRows.push(headers.join(','));

    for (const row of data) {
      const values = headers.map(header => {
        const value = row[header];
        return `"${value}"`;
      });
      csvRows.push(values.join(','));
    }

    return csvRows.join('\n');
  }

  async testIntegracion(tipo, config) {
    await delay(1000);
    
    const resultados = {
      bittacora: {
        success: true,
        message: 'Conexión exitosa con Bittácora',
        latencia: Math.floor(Math.random() * 200) + 100
      },
      email: {
        success: true,
        message: 'Servidor SMTP configurado correctamente',
        latencia: Math.floor(Math.random() * 300) + 150
      },
      sms: {
        success: false,
        message: 'API Key no configurada',
        latencia: 0
      }
    };

    return resultados[tipo] || { success: false, message: 'Tipo de integración no válido' };
  }

  async sincronizarBittacora(usuario) {
    await delay(2000);
    const data = this.getData();
    
    if (!data || !data.configuracion) {
      throw new Error('Configuración no encontrada');
    }

    data.configuracion.integraciones.bittacora.ultimaSincronizacion = new Date().toISOString();
    this.setData(data);

    this.addAuditLog(
      usuario,
      'sincronizar_bittacora',
      'integracion',
      'bittacora',
      'Sincronización manual con Bittácora'
    );

    return {
      success: true,
      message: 'Sincronización completada',
      garantiasSincronizadas: Math.floor(Math.random() * 50) + 10,
      averiasSincronizadas: Math.floor(Math.random() * 30) + 5
    };
  }

  // ============================================
  // ENDPOINTS PARA AGENTE COMERCIAL
  // ============================================

  /**
   * Obtener todos los vendedores
   */
  async getVendedores() {
    await this.delay(300);
    const data = this.getData();
    return {
      success: true,
      data: data.vendedores || []
    };
  }

  /**
   * Obtener un vendedor por ID
   */
  async getVendedor(vendedorId) {
    await this.delay(300);
    const data = this.getData();
    const vendedor = data.vendedores?.find(v => v.id === vendedorId);

    if (!vendedor) {
      throw new Error('Vendedor no encontrado');
    }

    return {
      success: true,
      data: vendedor
    };
  }

  /**
   * Crear un nuevo vendedor
   */
  async createVendedor(vendedorData, adminEmail) {
    await this.delay(500);
    const data = this.getData();

    // Validar email único
    const emailExiste = data.vendedores?.some(v => v.email === vendedorData.email);
    if (emailExiste) {
      throw new Error('El email ya está registrado');
    }

    const nuevoVendedor = {
      id: (data.vendedores?.length || 0) + 1,
      ...vendedorData,
      fechaAlta: new Date().toISOString().split('T')[0],
      totalVentas: 0,
      ventasMesActual: 0,
      activo: true
    };

    data.vendedores = [...(data.vendedores || []), nuevoVendedor];
    this.setData(data);

    this.addAuditLog(
      adminEmail,
      'crear_vendedor',
      'vendedor',
      nuevoVendedor.id,
      `Vendedor creado: ${nuevoVendedor.nombre}`
    );

    return {
      success: true,
      data: nuevoVendedor
    };
  }

  /**
   * Actualizar un vendedor
   */
  async updateVendedor(vendedorId, valores, adminEmail) {
    await this.delay(500);
    const data = this.getData();

    const index = data.vendedores?.findIndex(v => v.id === vendedorId);
    if (index === -1 || index === undefined) {
      throw new Error('Vendedor no encontrado');
    }

    // Validar email único si se está cambiando
    if (valores.email && valores.email !== data.vendedores[index].email) {
      const emailExiste = data.vendedores.some(v => v.id !== vendedorId && v.email === valores.email);
      if (emailExiste) {
        throw new Error('El email ya está registrado');
      }
    }

    data.vendedores[index] = {
      ...data.vendedores[index],
      ...valores
    };

    this.setData(data);

    this.addAuditLog(
      adminEmail,
      'actualizar_vendedor',
      'vendedor',
      vendedorId,
      `Vendedor actualizado: ${data.vendedores[index].nombre}`
    );

    return {
      success: true,
      data: data.vendedores[index]
    };
  }

  /**
   * Eliminar un vendedor
   */
  async deleteVendedor(vendedorId, adminEmail) {
    await this.delay(500);
    const data = this.getData();

    const vendedor = data.vendedores?.find(v => v.id === vendedorId);
    if (!vendedor) {
      throw new Error('Vendedor no encontrado');
    }

    data.vendedores = data.vendedores.filter(v => v.id !== vendedorId);
    this.setData(data);

    this.addAuditLog(
      adminEmail,
      'eliminar_vendedor',
      'vendedor',
      vendedorId,
      `Vendedor eliminado: ${vendedor.nombre}`
    );

    return {
      success: true,
      message: 'Vendedor eliminado correctamente'
    };
  }

  /**
   * Obtener márgenes por nivel de cobertura
   */
  async getMargenes() {
    await this.delay(300);
    const data = this.getData();
    return {
      success: true,
      data: data.margenes || []
    };
  }

  /**
   * Actualizar márgenes
   */
  async updateMargenes(margenes, adminEmail) {
    await this.delay(500);
    const data = this.getData();

    data.margenes = margenes;
    this.setData(data);

    this.addAuditLog(
      adminEmail,
      'actualizar_margenes',
      'configuracion',
      'margenes',
      'Márgenes actualizados'
    );

    return {
      success: true,
      data: margenes
    };
  }

  /**
   * Obtener liquidaciones con filtros
   */
  async getLiquidaciones(filtros = {}) {
    await this.delay(500);
    const data = this.getData();
    let liquidaciones = data.liquidaciones || [];

    // Aplicar filtros
    if (filtros.vendedorId) {
      liquidaciones = liquidaciones.filter(liq => liq.vendedorId === filtros.vendedorId);
    }

    if (filtros.estado) {
      liquidaciones = liquidaciones.filter(liq => liq.estado === filtros.estado);
    }

    if (filtros.mes) {
      liquidaciones = liquidaciones.filter(liq => liq.mes === filtros.mes);
    }

    if (filtros.year) {
      liquidaciones = liquidaciones.filter(liq => liq.year === filtros.year);
    }

    if (filtros.fechaDesde) {
      liquidaciones = liquidaciones.filter(liq => liq.fechaGeneracion >= filtros.fechaDesde);
    }

    if (filtros.fechaHasta) {
      liquidaciones = liquidaciones.filter(liq => liq.fechaGeneracion <= filtros.fechaHasta);
    }

    // Ordenar por fecha descendente
    liquidaciones.sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return b.mes - a.mes;
    });

    // Paginación
    const limit = filtros.limit || 50;
    const offset = filtros.offset || 0;
    const total = liquidaciones.length;
    liquidaciones = liquidaciones.slice(offset, offset + limit);

    return {
      success: true,
      data: liquidaciones,
      total,
      offset,
      limit
    };
  }

  /**
   * Obtener una liquidación por ID
   */
  async getLiquidacion(liquidacionId) {
    await this.delay(300);
    const data = this.getData();
    const liquidacion = data.liquidaciones?.find(liq => liq.id === liquidacionId);

    if (!liquidacion) {
      throw new Error('Liquidación no encontrada');
    }

    return {
      success: true,
      data: liquidacion
    };
  }

  /**
   * Generar liquidación para un vendedor y periodo
   */
  async generarLiquidacion(vendedorId, mes, year, adminEmail) {
    await this.delay(1000);
    const data = this.getData();

    // Verificar que el vendedor existe
    const vendedor = data.vendedores?.find(v => v.id === vendedorId);
    if (!vendedor) {
      throw new Error('Vendedor no encontrado');
    }

    // Verificar que no existe ya una liquidación para ese periodo
    const liquidacionExiste = data.liquidaciones?.some(
      liq => liq.vendedorId === vendedorId && liq.mes === mes && liq.year === year
    );

    if (liquidacionExiste) {
      throw new Error('Ya existe una liquidación para este periodo');
    }

    // Obtener ventas del periodo (filtrar garantías del vendedor en ese mes)
    const garantiasVendedor = data.garantias?.filter(g => {
      const fechaInicio = new Date(g.fechaInicio);
      return fechaInicio.getMonth() + 1 === mes &&
             fechaInicio.getFullYear() === year &&
             g.vendedor === vendedor.nombre; // Asumiendo que el nombre coincide
    }) || [];

    // Calcular ventas
    const ventas = garantiasVendedor.map(garantia => {
      const nivel = data.nivelesCobertura?.find(n => n.nombre === garantia.nivelCobertura);
      const margen = data.margenes?.find(m => m.nivel === garantia.nivelCobertura);

      return {
        garantiaId: garantia.id,
        fecha: garantia.fechaInicio,
        cliente: garantia.propietario.nombre,
        vehiculo: `${garantia.vehiculo.marca} ${garantia.vehiculo.modelo}`,
        nivel: garantia.nivelCobertura,
        precioVenta: nivel?.precio || 0,
        comisionPorcentaje: margen?.comisionVendedor || vendedor.comision,
        comisionImporte: parseFloat((((nivel?.precio || 0) * (margen?.comisionVendedor || vendedor.comision)) / 100).toFixed(2))
      };
    });

    // Calcular deducciones (averías del periodo)
    const averiasPeriodo = data.averias?.filter(averia => {
      const fechaAveria = new Date(averia.fechaAveria);
      const garantia = data.garantias?.find(g => g.id === averia.garantiaId);
      return fechaAveria.getMonth() + 1 === mes &&
             fechaAveria.getFullYear() === year &&
             garantia?.vendedor === vendedor.nombre &&
             averia.estado === 'rechazada'; // Solo averías rechazadas se deducen
    }) || [];

    const deducciones = averiasPeriodo.map(averia => ({
      tipo: 'Avería',
      concepto: `Avería rechazada: ${averia.id}`,
      importe: parseFloat((averia.costeEstimado * 0.1).toFixed(2)), // 10% del coste como penalización
      fecha: averia.fechaAveria
    }));

    // Calcular totales
    const totalVentas = ventas.reduce((sum, v) => sum + v.precioVenta, 0);
    const totalComisionesBrutas = ventas.reduce((sum, v) => sum + v.comisionImporte, 0);
    const totalDeducciones = deducciones.reduce((sum, d) => sum + d.importe, 0);
    const comisionNeta = totalComisionesBrutas - totalDeducciones;

    const nuevaLiquidacion = {
      id: `LIQ-${year}${String(mes).padStart(2, '0')}-${vendedorId}`,
      vendedorId: vendedorId,
      vendedorNombre: vendedor.nombre,
      mes: mes,
      year: year,
      periodo: `${String(mes).padStart(2, '0')}/${year}`,
      fechaGeneracion: new Date().toISOString().split('T')[0],
      fechaPago: null,
      numeroVentas: ventas.length,
      totalVentas: parseFloat(totalVentas.toFixed(2)),
      totalComisionesBrutas: parseFloat(totalComisionesBrutas.toFixed(2)),
      totalDeducciones: parseFloat(totalDeducciones.toFixed(2)),
      comisionNeta: parseFloat(comisionNeta.toFixed(2)),
      ventas: ventas,
      deducciones: deducciones,
      estado: 'pendiente',
      observaciones: null
    };

    data.liquidaciones = [...(data.liquidaciones || []), nuevaLiquidacion];
    this.setData(data);

    this.addAuditLog(
      adminEmail,
      'generar_liquidacion',
      'liquidacion',
      nuevaLiquidacion.id,
      `Liquidación generada para ${vendedor.nombre} - ${nuevaLiquidacion.periodo}`
    );

    return {
      success: true,
      data: nuevaLiquidacion
    };
  }

  /**
   * Actualizar estado de liquidación
   */
  async updateEstadoLiquidacion(liquidacionId, nuevoEstado, adminEmail, observaciones = null) {
    await this.delay(500);
    const data = this.getData();

    const index = data.liquidaciones?.findIndex(liq => liq.id === liquidacionId);
    if (index === -1 || index === undefined) {
      throw new Error('Liquidación no encontrada');
    }

    data.liquidaciones[index].estado = nuevoEstado;
    data.liquidaciones[index].observaciones = observaciones;

    if (nuevoEstado === 'pagada') {
      data.liquidaciones[index].fechaPago = new Date().toISOString().split('T')[0];
    }

    this.setData(data);

    this.addAuditLog(
      adminEmail,
      'actualizar_liquidacion',
      'liquidacion',
      liquidacionId,
      `Estado actualizado a: ${nuevoEstado}`
    );

    return {
      success: true,
      data: data.liquidaciones[index]
    };
  }

  /**
   * Exportar liquidación a PDF (simulado)
   */
  async exportarLiquidacionPDF(liquidacionId) {
    await this.delay(1000);
    const data = this.getData();
    const liquidacion = data.liquidaciones?.find(liq => liq.id === liquidacionId);

    if (!liquidacion) {
      throw new Error('Liquidación no encontrada');
    }

    // Simular generación de PDF
    console.log('Generando PDF de liquidación:', liquidacion);

    return {
      success: true,
      message: 'PDF generado correctamente',
      filename: `liquidacion_${liquidacion.id}.pdf`
    };
  }

  /**
   * Obtener alertas con filtros
   */
  async getAlertas(filtros = {}) {
    await this.delay(300);
    const data = this.getData();
    let alertas = data.alertas || [];

    // Aplicar filtros
    if (filtros.tipo) {
      alertas = alertas.filter(alerta => alerta.tipo === filtros.tipo);
    }

    if (filtros.prioridad) {
      alertas = alertas.filter(alerta => alerta.prioridad === filtros.prioridad);
    }

    if (filtros.leida !== undefined) {
      alertas = alertas.filter(alerta => alerta.leida === filtros.leida);
    }

    // Ordenar por fecha descendente
    alertas.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));

    // Paginación
    const limit = filtros.limit || 50;
    const offset = filtros.offset || 0;
    const total = alertas.length;
    const noLeidas = alertas.filter(a => !a.leida).length;
    alertas = alertas.slice(offset, offset + limit);

    return {
      success: true,
      data: alertas,
      total,
      noLeidas,
      offset,
      limit
    };
  }

  /**
   * Marcar alerta como leída
   */
  async marcarAlertaLeida(alertaId, usuario) {
    await this.delay(200);
    const data = this.getData();

    const index = data.alertas?.findIndex(alerta => alerta.id === alertaId);
    if (index === -1 || index === undefined) {
      throw new Error('Alerta no encontrada');
    }

    data.alertas[index].leida = true;
    this.setData(data);

    return {
      success: true,
      data: data.alertas[index]
    };
  }

  /**
   * Marcar todas las alertas como leídas
   */
  async marcarTodasAlertasLeidas(usuario) {
    await this.delay(300);
    const data = this.getData();

    if (data.alertas) {
      data.alertas = data.alertas.map(alerta => ({
        ...alerta,
        leida: true
      }));
      this.setData(data);
    }

    return {
      success: true,
      message: 'Todas las alertas marcadas como leídas'
    };
  }

  /**
   * Eliminar una alerta
   */
  async eliminarAlerta(alertaId, usuario) {
    await this.delay(200);
    const data = this.getData();

    const alerta = data.alertas?.find(a => a.id === alertaId);
    if (!alerta) {
      throw new Error('Alerta no encontrada');
    }

    data.alertas = data.alertas.filter(a => a.id !== alertaId);
    this.setData(data);

    return {
      success: true,
      message: 'Alerta eliminada correctamente'
    };
  }

  /**
   * Obtener estadísticas del agente comercial
   */
  async getEstadisticasAgente(agenteEmail) {
    await this.delay(500);
    const data = this.getData();

    const currentDate = new Date();
    const mesActual = currentDate.getMonth() + 1;
    const yearActual = currentDate.getFullYear();

    // Liquidaciones del mes actual
    const liquidacionesMesActual = data.liquidaciones?.filter(
      liq => liq.mes === mesActual && liq.year === yearActual
    ) || [];

    // Calcular totales
    const totalVentasMes = liquidacionesMesActual.reduce((sum, liq) => sum + liq.numeroVentas, 0);
    const totalImporteMes = liquidacionesMesActual.reduce((sum, liq) => sum + liq.totalVentas, 0);
    const totalComisionesMes = liquidacionesMesActual.reduce((sum, liq) => sum + liq.comisionNeta, 0);

    // Liquidaciones pendientes
    const liquidacionesPendientes = data.liquidaciones?.filter(
      liq => liq.estado === 'pendiente' || liq.estado === 'en_revision'
    ) || [];

    const saldoPendiente = liquidacionesPendientes.reduce((sum, liq) => sum + liq.comisionNeta, 0);

    // Alertas no leídas
    const alertasNoLeidas = data.alertas?.filter(a => !a.leida).length || 0;

    // Vendedores activos
    const vendedoresActivos = data.vendedores?.filter(v => v.activo).length || 0;

    return {
      success: true,
      data: {
        mesActual: {
          ventas: totalVentasMes,
          importe: parseFloat(totalImporteMes.toFixed(2)),
          comisiones: parseFloat(totalComisionesMes.toFixed(2))
        },
        liquidacionesPendientes: liquidacionesPendientes.length,
        saldoPendiente: parseFloat(saldoPendiente.toFixed(2)),
        alertasNoLeidas: alertasNoLeidas,
        vendedoresActivos: vendedoresActivos
      }
    };
  }

  // ============================================
  // ENDPOINTS PARA ROL VENDEDOR
  // ============================================

  async buscarVehiculo(query) {
    await delay(300);
    const data = this.getData();
    if (!data || !data.vehiculosMock) {
      throw new Error('Base de datos de vehículos no disponible');
    }

    const queryLower = query.toLowerCase();
    const resultados = data.vehiculosMock.filter(v =>
      v.matricula.toLowerCase().includes(queryLower) ||
      v.marca.toLowerCase().includes(queryLower) ||
      v.modelo.toLowerCase().includes(queryLower) ||
      v.bastidor.toLowerCase().includes(queryLower)
    ).slice(0, 10);

    return {
      success: true,
      data: resultados,
      total: resultados.length
    };
  }

  async getVehiculoPorMatricula(matricula) {
    await delay(200);
    const data = this.getData();
    if (!data || !data.vehiculosMock) {
      throw new Error('Base de datos de vehículos no disponible');
    }

    const vehiculo = data.vehiculosMock.find(v =>
      v.matricula.toUpperCase() === matricula.toUpperCase()
    );

    if (!vehiculo) {
      throw new Error('Vehículo no encontrado');
    }

    return {
      success: true,
      data: vehiculo
    };
  }

  async calcularRentabilidad(precioVentaPropio, costeBase) {
    await delay(100);

    const rentabilidad = precioVentaPropio - costeBase;
    const porcentaje = ((rentabilidad / precioVentaPropio) * 100).toFixed(2);

    return {
      success: true,
      data: {
        rentabilidad: parseFloat(rentabilidad.toFixed(2)),
        porcentajeRentabilidad: parseFloat(porcentaje),
        margenBruto: rentabilidad,
        costeBase,
        precioVenta: precioVentaPropio,
        recomendacion: parseFloat(porcentaje) < 20 ? 'bajo' : parseFloat(porcentaje) < 35 ? 'medio' : 'alto'
      }
    };
  }

  async getProyeccionBeneficios(vendedorEmail, dias = 30) {
    await delay(400);
    const data = this.getData();
    if (!data || !data.garantias) {
      throw new Error('Datos no disponibles');
    }

    const fechaLimite = new Date();
    fechaLimite.setDate(fechaLimite.getDate() - dias);

    const ventasVendedor = data.garantias.filter(g => {
      const fechaAlta = new Date(g.fechaAlta);
      return g.vendedor === vendedorEmail && fechaAlta >= fechaLimite;
    });

    if (ventasVendedor.length === 0) {
      return {
        success: true,
        data: {
          ventasUltimos30Dias: 0,
          beneficioTotal: 0,
          beneficioPromedioPorVenta: 0,
          proyeccionMensual: 0,
          proyeccionAnual: 0
        }
      };
    }

    const beneficioTotal = ventasVendedor.reduce((sum, g) => sum + (g.rentabilidad || 0), 0);
    const beneficioPromedio = beneficioTotal / ventasVendedor.length;

    const ventasPorDia = ventasVendedor.length / dias;
    const proyeccionMensual = ventasPorDia * 30 * beneficioPromedio;
    const proyeccionAnual = proyeccionMensual * 12;

    return {
      success: true,
      data: {
        ventasUltimos30Dias: ventasVendedor.length,
        beneficioTotal: parseFloat(beneficioTotal.toFixed(2)),
        beneficioPromedioPorVenta: parseFloat(beneficioPromedio.toFixed(2)),
        ventasPorDia: parseFloat(ventasPorDia.toFixed(2)),
        proyeccionMensual: parseFloat(proyeccionMensual.toFixed(2)),
        proyeccionAnual: parseFloat(proyeccionAnual.toFixed(2))
      }
    };
  }

  async crearGarantiaConFirma(garantiaData, firmaDigital, usuario) {
    await delay(600);
    const data = this.getData();
    if (!data) {
      throw new Error('Datos no disponibles');
    }

    const nuevaGarantia = {
      ...garantiaData,
      id: `GAR-${Date.now()}`,
      numeroGarantia: `GAR-${Math.floor(100000 + Math.random() * 900000)}`,
      fechaAlta: new Date().toISOString(),
      estado: 'activa',
      firmaDigital: {
        ...firmaDigital,
        timestamp: new Date().toISOString(),
        hash: Math.random().toString(36).substring(2) + Math.random().toString(36).substring(2)
      }
    };

    data.garantias = [nuevaGarantia, ...data.garantias];
    this.setData(data);

    this.addAuditLog(
      usuario,
      'crear_garantia',
      'garantia',
      nuevaGarantia.id,
      `Garantía creada con firma digital (${firmaDigital.metodo})`
    );

    return {
      success: true,
      data: nuevaGarantia,
      message: 'Garantía creada exitosamente con firma digital'
    };
  }

  // ============================================
  // ENDPOINTS PARA ROL TÉCNICO
  // ============================================

  async getAveria(averiaId) {
    await delay(200);
    const data = this.getData();
    if (!data || !data.averias) {
      throw new Error('Datos no disponibles');
    }

    const averia = data.averias.find(a => a.id === averiaId);
    if (!averia) {
      throw new Error('Avería no encontrada');
    }

    return {
      success: true,
      data: averia
    };
  }

  async crearDiagnostico(averiaId, diagnosticoData, usuario) {
    await delay(500);
    const data = this.getData();
    if (!data || !data.averias) {
      throw new Error('Datos no disponibles');
    }

    const averiaIndex = data.averias.findIndex(a => a.id === averiaId);
    if (averiaIndex === -1) {
      throw new Error('Avería no encontrada');
    }

    const diagnostico = {
      id: `DIAG-${Date.now()}`,
      averiaId,
      ...diagnosticoData,
      timestamp: new Date().toISOString(),
      ip: '192.168.1.' + Math.floor(Math.random() * 255),
      hash: Math.random().toString(36).substring(2) + Math.random().toString(36).substring(2),
      tecnico: usuario
    };

    data.averias[averiaIndex].diagnostico = diagnostico;
    data.averias[averiaIndex].estado = 'diagnosticada';
    data.averias[averiaIndex].costeEstimado = diagnostico.costeTotal || diagnostico.coste;
    data.averias[averiaIndex].fechaDiagnostico = diagnostico.timestamp;
    data.averias[averiaIndex].tecnicoAsignado = usuario;

    this.setData(data);

    this.addAuditLog(
      usuario,
      'crear_diagnostico',
      'averia',
      averiaId,
      `Diagnóstico creado - Coste: ${diagnostico.costeTotal || diagnostico.coste}€`
    );

    return {
      success: true,
      data: data.averias[averiaIndex],
      message: 'Diagnóstico creado exitosamente'
    };
  }

  async actualizarDiagnostico(averiaId, diagnosticoData, usuario) {
    await delay(500);
    const data = this.getData();
    if (!data || !data.averias) {
      throw new Error('Datos no disponibles');
    }

    const averiaIndex = data.averias.findIndex(a => a.id === averiaId);
    if (averiaIndex === -1) {
      throw new Error('Avería no encontrada');
    }

    if (!data.averias[averiaIndex].diagnostico) {
      throw new Error('La avería no tiene diagnóstico previo');
    }

    data.averias[averiaIndex].diagnostico = {
      ...data.averias[averiaIndex].diagnostico,
      ...diagnosticoData,
      ultimaActualizacion: new Date().toISOString(),
      actualizadoPor: usuario
    };

    if (diagnosticoData.costeTotal || diagnosticoData.coste) {
      data.averias[averiaIndex].costeEstimado = diagnosticoData.costeTotal || diagnosticoData.coste;
    }

    this.setData(data);

    this.addAuditLog(
      usuario,
      'actualizar_diagnostico',
      'averia',
      averiaId,
      'Diagnóstico actualizado'
    );

    return {
      success: true,
      data: data.averias[averiaIndex],
      message: 'Diagnóstico actualizado exitosamente'
    };
  }

  async subirEvidencia(averiaId, evidenciaData, usuario) {
    await delay(800);
    const data = this.getData();
    if (!data || !data.averias) {
      throw new Error('Datos no disponibles');
    }

    const averiaIndex = data.averias.findIndex(a => a.id === averiaId);
    if (averiaIndex === -1) {
      throw new Error('Avería no encontrada');
    }

    const evidencia = {
      id: `EVD-${Date.now()}`,
      averiaId,
      ...evidenciaData,
      timestamp: new Date().toISOString(),
      hash: Math.random().toString(36).substring(2) + Math.random().toString(36).substring(2),
      uploadedBy: usuario
    };

    if (!data.averias[averiaIndex].evidencias) {
      data.averias[averiaIndex].evidencias = [];
    }

    data.averias[averiaIndex].evidencias.push(evidencia);
    data.averias[averiaIndex].ultimaActualizacion = new Date().toISOString();

    this.setData(data);

    this.addAuditLog(
      usuario,
      'subir_evidencia',
      'averia',
      averiaId,
      `Evidencia subida: ${evidencia.tipo} - ${evidencia.filename}`
    );

    return {
      success: true,
      data: evidencia,
      message: 'Evidencia subida exitosamente'
    };
  }

  async getEvidencias(averiaId) {
    await delay(300);
    const data = this.getData();
    if (!data || !data.averias) {
      throw new Error('Datos no disponibles');
    }

    const averia = data.averias.find(a => a.id === averiaId);
    if (!averia) {
      throw new Error('Avería no encontrada');
    }

    return {
      success: true,
      data: averia.evidencias || [],
      total: (averia.evidencias || []).length
    };
  }

  async eliminarEvidencia(averiaId, evidenciaId, usuario) {
    await delay(400);
    const data = this.getData();
    if (!data || !data.averias) {
      throw new Error('Datos no disponibles');
    }

    const averiaIndex = data.averias.findIndex(a => a.id === averiaId);
    if (averiaIndex === -1) {
      throw new Error('Avería no encontrada');
    }

    if (!data.averias[averiaIndex].evidencias) {
      throw new Error('No hay evidencias para eliminar');
    }

    const evidenciaIndex = data.averias[averiaIndex].evidencias.findIndex(e => e.id === evidenciaId);
    if (evidenciaIndex === -1) {
      throw new Error('Evidencia no encontrada');
    }

    const evidenciaEliminada = data.averias[averiaIndex].evidencias[evidenciaIndex];
    data.averias[averiaIndex].evidencias.splice(evidenciaIndex, 1);

    this.setData(data);

    this.addAuditLog(
      usuario,
      'eliminar_evidencia',
      'averia',
      averiaId,
      `Evidencia eliminada: ${evidenciaEliminada.filename}`
    );

    return {
      success: true,
      message: 'Evidencia eliminada exitosamente'
    };
  }

  async getLiquidacionesTecnicas(filtros = {}) {
    await delay(400);
    const data = this.getData();

    if (!data.liquidacionesTecnicas) {
      data.liquidacionesTecnicas = [];
      this.setData(data);
    }

    let liquidaciones = [...data.liquidacionesTecnicas];

    if (filtros.tecnicoId) {
      liquidaciones = liquidaciones.filter(l => l.tecnicoId === filtros.tecnicoId);
    }

    if (filtros.estado) {
      liquidaciones = liquidaciones.filter(l => l.estado === filtros.estado);
    }

    if (filtros.mes) {
      liquidaciones = liquidaciones.filter(l => l.mes === filtros.mes);
    }

    if (filtros.year) {
      liquidaciones = liquidaciones.filter(l => l.year === filtros.year);
    }

    const limit = filtros.limit || 50;
    const offset = filtros.offset || 0;
    const paginadas = liquidaciones.slice(offset, offset + limit);

    return {
      success: true,
      data: paginadas,
      total: liquidaciones.length,
      limit,
      offset
    };
  }

  async generarLiquidacionTecnica(tecnicoId, mes, year, usuario) {
    await delay(600);
    const data = this.getData();

    if (!data.liquidacionesTecnicas) {
      data.liquidacionesTecnicas = [];
    }

    const existente = data.liquidacionesTecnicas.find(
      l => l.tecnicoId === tecnicoId && l.mes === mes && l.year === year
    );

    if (existente) {
      throw new Error('Ya existe una liquidación para este técnico en este periodo');
    }

    const averiasDelPeriodo = data.averias.filter(a => {
      if (!a.fechaAutorizacion || a.estado !== 'finalizada') return false;
      const fecha = new Date(a.fechaAutorizacion);
      return fecha.getMonth() + 1 === mes && fecha.getFullYear() === year;
    });

    const averias = averiasDelPeriodo.map(a => ({
      averiaId: a.id,
      numeroAveria: a.numeroAveria,
      fecha: a.fechaAutorizacion,
      tipo: a.tipo,
      coste: a.costeReal || a.costeEstimado,
      comision: (a.costeReal || a.costeEstimado) * 0.08,
      estado: 'finalizada'
    }));

    const totalCoste = averias.reduce((sum, a) => sum + a.coste, 0);
    const totalComision = averias.reduce((sum, a) => sum + a.comision, 0);

    const liquidacion = {
      id: `LIQ-TEC-${year}${String(mes).padStart(2, '0')}-${tecnicoId}`,
      tecnicoId,
      tecnicoNombre: `Técnico ${tecnicoId}`,
      mes,
      year,
      periodo: `${String(mes).padStart(2, '0')}/${year}`,
      fechaGeneracion: new Date().toISOString(),
      fechaPago: null,
      numeroAverias: averias.length,
      totalCoste: parseFloat(totalCoste.toFixed(2)),
      totalComision: parseFloat(totalComision.toFixed(2)),
      averias,
      estado: 'pendiente',
      observaciones: null
    };

    data.liquidacionesTecnicas.push(liquidacion);
    this.setData(data);

    this.addAuditLog(
      usuario,
      'generar_liquidacion_tecnica',
      'liquidacion_tecnica',
      liquidacion.id,
      `Liquidación generada - ${averias.length} averías - ${totalComision.toFixed(2)}€`
    );

    return {
      success: true,
      data: liquidacion,
      message: 'Liquidación técnica generada exitosamente'
    };
  }

  async autorizarAveriaConFirma(averiaId, firmaDigital, usuario) {
    await delay(500);
    const data = this.getData();
    if (!data || !data.averias) {
      throw new Error('Datos no disponibles');
    }

    const averiaIndex = data.averias.findIndex(a => a.id === averiaId);
    if (averiaIndex === -1) {
      throw new Error('Avería no encontrada');
    }

    data.averias[averiaIndex].estado = 'autorizada';
    data.averias[averiaIndex].fechaAutorizacion = new Date().toISOString();
    data.averias[averiaIndex].autorizadoPor = usuario;
    data.averias[averiaIndex].firmaDigital = {
      ...firmaDigital,
      timestamp: new Date().toISOString(),
      hash: Math.random().toString(36).substring(2) + Math.random().toString(36).substring(2),
      autorizadoPor: usuario
    };

    this.setData(data);

    this.addAuditLog(
      usuario,
      'autorizar_averia',
      'averia',
      averiaId,
      `Avería autorizada con firma digital (${firmaDigital.metodo})`
    );

    return {
      success: true,
      data: data.averias[averiaIndex],
      message: 'Avería autorizada exitosamente'
    };
  }

  // ============================================
  // ENDPOINTS ROL CLIENTE
  // ============================================

  async getClienteByEmail(email) {
    await delay(300);
    const data = this.getData();
    if (!data || !data.clientes) {
      throw new Error('Datos no disponibles');
    }

    const cliente = data.clientes.find(c => c.email === email);
    if (!cliente) {
      throw new Error('Cliente no encontrado');
    }

    return {
      success: true,
      data: cliente,
      message: 'Cliente encontrado'
    };
  }

  async getClienteById(clienteId) {
    await delay(300);
    const data = this.getData();
    if (!data || !data.clientes) {
      throw new Error('Datos no disponibles');
    }

    const cliente = data.clientes.find(c => c.id === clienteId);
    if (!cliente) {
      throw new Error('Cliente no encontrado');
    }

    return {
      success: true,
      data: cliente,
      message: 'Cliente encontrado'
    };
  }

  async getGarantiaCliente(clienteId) {
    await delay(400);
    const data = this.getData();
    if (!data || !data.clientes || !data.garantias) {
      throw new Error('Datos no disponibles');
    }

    const cliente = data.clientes.find(c => c.id === clienteId);
    if (!cliente) {
      throw new Error('Cliente no encontrado');
    }

    const garantia = data.garantias.find(g => g.id === cliente.garantiaId);
    if (!garantia) {
      throw new Error('Garantía no encontrada');
    }

    const fechaActual = new Date();
    const fechaVencimiento = new Date(garantia.fechaVencimiento);
    const diasRestantes = Math.ceil((fechaVencimiento - fechaActual) / (1000 * 60 * 60 * 24));
    const vigente = fechaVencimiento > fechaActual;
    const proximaVencer = vigente && diasRestantes <= 30;

    const coberturas = {
      1: {
        nivel: 'Básica',
        componentes: ['Motor', 'Caja de cambios'],
        servicios: ['Asistencia en carretera 24h'],
        franquicia: 150
      },
      2: {
        nivel: 'Estándar',
        componentes: ['Motor', 'Caja de cambios', 'Sistema eléctrico', 'Suspensión'],
        servicios: ['Asistencia en carretera 24h', 'Vehículo de sustitución (3 días)'],
        franquicia: 100
      },
      3: {
        nivel: 'Premium',
        componentes: ['Motor', 'Caja de cambios', 'Sistema eléctrico', 'Suspensión', 'Dirección', 'Frenos', 'Climatización'],
        servicios: ['Asistencia en carretera 24h', 'Vehículo de sustitución (7 días)', 'Gestión de taller'],
        franquicia: 50
      },
      4: {
        nivel: 'Total',
        componentes: ['Motor', 'Caja de cambios', 'Sistema eléctrico', 'Suspensión', 'Dirección', 'Frenos', 'Climatización', 'Turbo', 'Sistema de inyección'],
        servicios: ['Asistencia en carretera 24h', 'Vehículo de sustitución (15 días)', 'Gestión de taller', 'Recogida y entrega a domicilio'],
        franquicia: 0
      }
    };

    const cobertura = coberturas[garantia.nivel];
    const averiasCliente = data.averias.filter(a => a.garantiaId === garantia.id);
    const averiasActivas = averiasCliente.filter(a => ['pendiente', 'en_curso', 'diagnosticada'].includes(a.estado));

    return {
      success: true,
      data: {
        garantia: {
          id: garantia.id,
          numeroGarantia: garantia.numeroGarantia,
          nivel: garantia.nivel,
          nivelNombre: garantia.nivelNombre,
          fechaAlta: garantia.fechaAlta,
          fechaVencimiento: garantia.fechaVencimiento,
          estado: garantia.estado,
          vigente,
          diasRestantes: vigente ? diasRestantes : 0,
          proximaVencer
        },
        vehiculo: {
          matricula: garantia.vehiculo.matricula,
          marca: garantia.vehiculo.marca,
          modelo: garantia.vehiculo.modelo,
          version: garantia.vehiculo.version,
          year: garantia.vehiculo.year,
          combustible: garantia.vehiculo.combustible,
          kilometros: garantia.vehiculo.kilometros
        },
        cobertura: {
          nivel: cobertura.nivel,
          componentesCubiertos: cobertura.componentes,
          serviciosIncluidos: cobertura.servicios,
          franquicia: cobertura.franquicia,
          duracion: '12 meses',
          kilometrosMaximos: 'Sin límite'
        },
        estadisticas: {
          numeroAverias: averiasCliente.length,
          averiasActivas: averiasActivas.length,
          ultimaRevision: garantia.fechaAlta
        }
      },
      message: 'Garantía consultada correctamente'
    };
  }

  async getAveriasCliente(clienteId) {
    await delay(400);
    const data = this.getData();
    if (!data || !data.clientes || !data.averias) {
      throw new Error('Datos no disponibles');
    }

    const cliente = data.clientes.find(c => c.id === clienteId);
    if (!cliente) {
      throw new Error('Cliente no encontrado');
    }

    const averiasCliente = data.averias.filter(a => a.garantiaId === cliente.garantiaId);

    const averiasFormateadas = averiasCliente.map(averia => ({
      id: averia.id,
      numeroAveria: averia.numeroAveria,
      tipo: averia.tipo,
      descripcion: averia.descripcion,
      estado: averia.estado,
      fechaReporte: averia.fechaReporte,
      fechaAutorizacion: averia.fechaAutorizacion,
      cubierta: averia.cubierta,
      costeEstimado: averia.costeEstimado,
      taller: averia.taller,
      diagnostico: averia.diagnostico ? {
        descripcion: averia.diagnostico.descripcion,
        coste: averia.diagnostico.coste,
        timestamp: averia.diagnostico.timestamp
      } : null,
      tieneEvidencias: averia.evidencias && averia.evidencias.length > 0,
      numeroEvidencias: averia.evidencias ? averia.evidencias.length : 0
    }));

    return {
      success: true,
      data: averiasFormateadas,
      message: 'Averías consultadas correctamente'
    };
  }

  async getDocumentosCliente(clienteId) {
    await delay(300);
    const data = this.getData();
    if (!data || !data.documentosClientes) {
      throw new Error('Datos no disponibles');
    }

    const documentos = data.documentosClientes.filter(d => d.clienteId === clienteId && d.visible);

    return {
      success: true,
      data: documentos,
      message: 'Documentos consultados correctamente'
    };
  }

  async getDocumentoCliente(clienteId, documentoId) {
    await delay(400);
    const data = this.getData();
    if (!data || !data.documentosClientes) {
      throw new Error('Datos no disponibles');
    }

    const documento = data.documentosClientes.find(d => d.id === documentoId);

    if (!documento) {
      throw new Error('Documento no encontrado');
    }

    if (documento.clienteId !== clienteId) {
      throw new Error('No tiene permisos para acceder a este documento');
    }

    if (!documento.visible) {
      throw new Error('Documento no disponible');
    }

    documento.descargas = (documento.descargas || 0) + 1;
    documento.ultimaDescarga = new Date().toISOString();
    this.setData(data);

    return {
      success: true,
      data: documento,
      message: 'Documento obtenido correctamente'
    };
  }

  async descargarCertificadoPDF(clienteId, garantiaId) {
    await delay(600);
    const data = this.getData();
    if (!data || !data.clientes || !data.garantias) {
      throw new Error('Datos no disponibles');
    }

    const cliente = data.clientes.find(c => c.id === clienteId);
    if (!cliente) {
      throw new Error('Cliente no encontrado');
    }

    if (cliente.garantiaId !== garantiaId) {
      throw new Error('No tiene permisos para acceder a esta garantía');
    }

    const garantia = data.garantias.find(g => g.id === garantiaId);
    if (!garantia) {
      throw new Error('Garantía no encontrada');
    }

    const certificado = {
      filename: `certificado_garantia_${garantia.numeroGarantia}.pdf`,
      url: `https://storage.rdgarantia.com/certificados/${garantia.id}.pdf`,
      generadoEn: new Date().toISOString(),
      garantia: {
        numero: garantia.numeroGarantia,
        nivel: garantia.nivelNombre,
        fechaAlta: garantia.fechaAlta,
        fechaVencimiento: garantia.fechaVencimiento
      },
      vehiculo: garantia.vehiculo,
      cliente: {
        nombre: cliente.nombre,
        dni: cliente.dni
      }
    };

    this.addAuditLog(
      cliente.email,
      'descargar_certificado',
      'documento',
      garantia.id,
      `Certificado descargado: ${garantia.numeroGarantia}`
    );

    return {
      success: true,
      data: certificado,
      message: 'Certificado generado correctamente'
    };
  }

  async getNotificacionesCliente(clienteId, filtros = {}) {
    await delay(300);
    const data = this.getData();
    if (!data || !data.notificacionesClientes) {
      throw new Error('Datos no disponibles');
    }

    let notificaciones = data.notificacionesClientes.filter(n => n.clienteId === clienteId);

    if (filtros.tipo) {
      notificaciones = notificaciones.filter(n => n.tipo === filtros.tipo);
    }

    if (filtros.leida !== undefined) {
      notificaciones = notificaciones.filter(n => n.leida === filtros.leida);
    }

    if (filtros.prioridad) {
      notificaciones = notificaciones.filter(n => n.prioridad === filtros.prioridad);
    }

    notificaciones.sort((a, b) => new Date(b.fechaCreacion) - new Date(a.fechaCreacion));

    const noLeidas = notificaciones.filter(n => !n.leida).length;

    return {
      success: true,
      data: {
        notificaciones,
        total: notificaciones.length,
        noLeidas
      },
      message: 'Notificaciones consultadas correctamente'
    };
  }

  async marcarNotificacionLeida(clienteId, notificacionId) {
    await delay(200);
    const data = this.getData();
    if (!data || !data.notificacionesClientes) {
      throw new Error('Datos no disponibles');
    }

    const notificacion = data.notificacionesClientes.find(n => n.id === notificacionId);

    if (!notificacion) {
      throw new Error('Notificación no encontrada');
    }

    if (notificacion.clienteId !== clienteId) {
      throw new Error('No tiene permisos para acceder a esta notificación');
    }

    notificacion.leida = true;
    notificacion.fechaLectura = new Date().toISOString();
    this.setData(data);

    return {
      success: true,
      data: notificacion,
      message: 'Notificación marcada como leída'
    };
  }

  async marcarTodasNotificacionesLeidas(clienteId) {
    await delay(300);
    const data = this.getData();
    if (!data || !data.notificacionesClientes) {
      throw new Error('Datos no disponibles');
    }

    const notificaciones = data.notificacionesClientes.filter(n => n.clienteId === clienteId && !n.leida);

    notificaciones.forEach(n => {
      n.leida = true;
      n.fechaLectura = new Date().toISOString();
    });

    this.setData(data);

    return {
      success: true,
      data: { marcadas: notificaciones.length },
      message: `${notificaciones.length} notificaciones marcadas como leídas`
    };
  }

  async actualizarConsentimiento(clienteId, consentimiento) {
    await delay(400);
    const data = this.getData();
    if (!data || !data.clientes) {
      throw new Error('Datos no disponibles');
    }

    const cliente = data.clientes.find(c => c.id === clienteId);
    if (!cliente) {
      throw new Error('Cliente no encontrado');
    }

    cliente.consentimiento = {
      ...cliente.consentimiento,
      aceptado: consentimiento.aceptado,
      fecha: new Date().toISOString(),
      ip: consentimiento.ip || '192.168.1.' + Math.floor(Math.random() * 255),
      hash: Math.random().toString(36).substring(2) + Math.random().toString(36).substring(2),
      version: consentimiento.version || '1.0',
      tipo: consentimiento.tipo || 'terminos_y_condiciones',
      observaciones: consentimiento.observaciones || null
    };

    this.setData(data);

    this.addAuditLog(
      cliente.email,
      'actualizar_consentimiento',
      'cliente',
      clienteId,
      `Consentimiento ${consentimiento.aceptado ? 'aceptado' : 'rechazado'}`
    );

    return {
      success: true,
      data: cliente.consentimiento,
      message: 'Consentimiento actualizado correctamente'
    };
  }

  async actualizarPreferenciasNotificaciones(clienteId, preferencias) {
    await delay(300);
    const data = this.getData();
    if (!data || !data.clientes) {
      throw new Error('Datos no disponibles');
    }

    const cliente = data.clientes.find(c => c.id === clienteId);
    if (!cliente) {
      throw new Error('Cliente no encontrado');
    }

    cliente.preferenciasNotificaciones = {
      ...cliente.preferenciasNotificaciones,
      ...preferencias
    };

    this.setData(data);

    this.addAuditLog(
      cliente.email,
      'actualizar_preferencias',
      'cliente',
      clienteId,
      'Preferencias de notificaciones actualizadas'
    );

    return {
      success: true,
      data: cliente.preferenciasNotificaciones,
      message: 'Preferencias actualizadas correctamente'
    };
  }

  async reportarAveria(clienteId, averiaData) {
    await delay(600);
    const data = this.getData();
    if (!data || !data.clientes || !data.averias) {
      throw new Error('Datos no disponibles');
    }

    const cliente = data.clientes.find(c => c.id === clienteId);
    if (!cliente) {
      throw new Error('Cliente no encontrado');
    }

    const garantia = data.garantias.find(g => g.id === cliente.garantiaId);
    if (!garantia) {
      throw new Error('Garantía no encontrada');
    }

    const fechaVencimiento = new Date(garantia.fechaVencimiento);
    if (fechaVencimiento < new Date()) {
      throw new Error('La garantía ha expirado');
    }

    const nuevaAveria = {
      id: `AVE-${Date.now()}`,
      numeroAveria: `AVE-${Math.floor(100000 + Math.random() * 900000)}`,
      garantiaId: garantia.id,
      tipo: averiaData.tipo,
      descripcion: averiaData.descripcion,
      fechaReporte: new Date().toISOString(),
      estado: 'pendiente',
      cubierta: null,
      costeEstimado: null,
      taller: averiaData.taller || null,
      reportadoPor: cliente.email,
      diagnostico: null,
      evidencias: [],
      firmaDigital: null
    };

    data.averias.push(nuevaAveria);
    this.setData(data);

    this.addAuditLog(
      cliente.email,
      'reportar_averia',
      'averia',
      nuevaAveria.id,
      `Avería reportada: ${averiaData.tipo}`
    );

    const notificacion = {
      id: `NOT-${Date.now()}`,
      clienteId: cliente.id,
      tipo: 'averia',
      titulo: 'Avería reportada correctamente',
      mensaje: `Su avería ${nuevaAveria.numeroAveria} ha sido registrada y será revisada por nuestro equipo técnico.`,
      prioridad: 'alta',
      leida: false,
      fechaCreacion: new Date().toISOString(),
      fechaLectura: null,
      accionRequerida: false,
      enlace: `/cliente/averias/${nuevaAveria.id}`,
      metadata: {
        origen: 'sistema',
        relacionadoCon: nuevaAveria.id
      }
    };

    data.notificacionesClientes.push(notificacion);
    this.setData(data);

    return {
      success: true,
      data: nuevaAveria,
      message: 'Avería reportada correctamente'
    };
  }
}

export const mockApi = new MockApiService();
export default mockApi;
